/**
 * Responsive design hooks and utilities
 * Mobile-first approach with Chakra UI v3 integration
 */

import * as React from 'react';

import { useBreakpointValue } from './use-breakpoint-value';

// For test environment compatibility - use custom implementation only
// This avoids dynamic imports and requirement issues while maintaining functionality

// Custom useMediaQuery implementation to avoid Chakra UI dependency
function useMediaQuery(queries: string | string[]): boolean[] {
  // Always call hooks first to comply with Rules of Hooks
  const [matches, setMatches] = React.useState<boolean[]>(
    Array.isArray(queries) ? queries.map(() => false) : [false]
  );

  // Stabilize queries to prevent infinite re-renders
  const queriesString = React.useMemo(() => {
    const queryList = Array.isArray(queries) ? queries : [queries];
    return JSON.stringify(queryList);
  }, [queries]);

  const queryList = React.useMemo(() => {
    return JSON.parse(queriesString) as string[];
  }, [queriesString]);

  // Note: Using custom implementation for all environments for consistency

  React.useEffect(() => {
    // Early return if no window (SSR)
    if (typeof window === 'undefined') return;

    const mediaQueryLists = queryList.map((query) => window.matchMedia(query));

    const updateMatches = () => {
      const newMatches = mediaQueryLists.map((mql) => mql.matches);
      setMatches((prev) => {
        // Only update if matches actually changed to prevent unnecessary re-renders
        if (JSON.stringify(prev) !== JSON.stringify(newMatches)) {
          return newMatches;
        }
        return prev;
      });
    };

    // Set initial values
    updateMatches();

    // Add listeners
    mediaQueryLists.forEach((mql) => {
      mql.addEventListener('change', updateMatches);
    });

    return () => {
      mediaQueryLists.forEach((mql) => {
        mql.removeEventListener('change', updateMatches);
      });
    };
  }, [queryList, queriesString]); // Use stable references

  return matches;
}

/**
 * Breakpoint definitions
 */
export const breakpoints = {
  base: 0, // 0px
  sm: 640, // 640px
  md: 768, // 768px
  lg: 1024, // 1024px
  xl: 1280, // 1280px
  '2xl': 1536, // 1536px
} as const;

export type Breakpoint = keyof typeof breakpoints;

/**
 * Device type detection hook
 */
export function useDeviceType() {
  const [isMobile] = useMediaQuery(['(max-width: 767px)']);
  const [isTablet] = useMediaQuery(['(min-width: 768px) and (max-width: 1023px)']);
  const [isDesktop] = useMediaQuery(['(min-width: 1024px)']);
  const [isLargeDesktop] = useMediaQuery(['(min-width: 1280px)']);

  return {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
  };
}

/**
 * Orientation detection hook
 */
export function useOrientation() {
  const [isPortrait] = useMediaQuery(['(orientation: portrait)']);
  const [isLandscape] = useMediaQuery(['(orientation: landscape)']);

  return {
    isPortrait,
    isLandscape,
    orientation: isPortrait ? 'portrait' : 'landscape',
  };
}

/**
 * Touch device detection hook
 */
export function useTouchDevice() {
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);

  React.useEffect(() => {
    const msMaxTouchPoints = (navigator as Navigator & { msMaxTouchPoints?: number })
      .msMaxTouchPoints;
    setIsTouchDevice(
      'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (msMaxTouchPoints !== undefined && msMaxTouchPoints > 0)
    );
  }, []);

  return isTouchDevice;
}

/**
 * Hover-capability hook. Distinct from `useTouchDevice` — a hybrid
 * device (Surface laptop, iPad with trackpad) reports
 * `maxTouchPoints > 0` AND has a hover-capable pointer. Use this for
 * hover-only behaviors (prefetch on mouseenter, tooltip-on-hover etc.)
 * so hybrid users get the desktop-class UX rather than being lumped
 * into mobile.
 *
 * Driven by `(hover: hover) and (pointer: fine)` — the W3C-standard
 * way to ask "is this a precise hover-capable pointer". Server render
 * defaults to `true` (desktop bias) to match SSR-then-hydrate flow;
 * the effect re-checks on mount.
 */
export function useHoverCapable() {
  const [isHoverCapable, setIsHoverCapable] = React.useState(true);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    setIsHoverCapable(query.matches);
    const handler = (e: MediaQueryListEvent) => setIsHoverCapable(e.matches);
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, []);

  return isHoverCapable;
}

/**
 * Window size hook with debouncing
 */
export interface WindowSize {
  width: number;
  height: number;
}

export function useWindowSize(debounceMs: number = 150): WindowSize {
  const [windowSize, setWindowSize] = React.useState<WindowSize>({
    width: 0,
    height: 0,
  });

  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial size
    handleResize();

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, debounceMs);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [debounceMs]);

  return windowSize;
}

/**
 * Responsive value hook with type safety
 */
export function useResponsiveValue<T>(values: Partial<Record<Breakpoint, T>> & { base: T }): T {
  const breakpointValue = useBreakpointValue(values);
  return breakpointValue ?? values.base;
}

/**
 * Container query hook (for component-level responsiveness)
 */
export function useContainerQuery(
  ref: React.RefObject<HTMLElement>,
  queries: Record<string, string>
) {
  const [matches, setMatches] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!ref.current || !('ResizeObserver' in window)) return;

    const element = ref.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width } = entry.contentRect;
      const newMatches: Record<string, boolean> = {};

      Object.entries(queries).forEach(([name, query]) => {
        // Simple query parser (supports min-width, max-width)
        const minWidthMatch = query.match(/min-width:\s*(\d+)px/);
        const maxWidthMatch = query.match(/max-width:\s*(\d+)px/);

        let matches = true;
        if (minWidthMatch) {
          matches = matches && width >= parseInt(minWidthMatch[1]);
        }
        if (maxWidthMatch) {
          matches = matches && width <= parseInt(maxWidthMatch[1]);
        }

        newMatches[name] = matches;
      });

      setMatches(newMatches);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, queries]);

  return matches;
}

/**
 * Viewport height hook (handles mobile browser chrome)
 */
export function useViewportHeight() {
  const [viewportHeight, setViewportHeight] = React.useState<number>(0);

  React.useEffect(() => {
    const updateViewportHeight = () => {
      // Use visualViewport API if available (better for mobile)
      const height = window.visualViewport?.height ?? window.innerHeight;
      setViewportHeight(height);

      // Set CSS custom property for use in styles
      document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
    };

    updateViewportHeight();

    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  return viewportHeight;
}

/**
 * Responsive columns hook for grids
 */
export function useResponsiveColumns(
  itemCount: number,
  options?: {
    minColumns?: Partial<Record<Breakpoint, number>>;
    maxColumns?: Partial<Record<Breakpoint, number>>;
  }
) {
  const defaultMinColumns = {
    base: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
  };

  const defaultMaxColumns = {
    base: 1,
    sm: 2,
    md: 4,
    lg: 6,
    xl: 8,
  };

  const minColumns = useResponsiveValue({
    ...defaultMinColumns,
    ...options?.minColumns,
  });

  const maxColumns = useResponsiveValue({
    ...defaultMaxColumns,
    ...options?.maxColumns,
  });

  const optimalColumns = Math.min(
    Math.max(minColumns, Math.ceil(Math.sqrt(itemCount))),
    maxColumns
  );

  return {
    columns: optimalColumns,
    minColumns,
    maxColumns,
  };
}

/**
 * Safe area insets hook (for devices with notches/home indicators)
 */
export function useSafeAreaInsets() {
  const [insets, setInsets] = React.useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  React.useEffect(() => {
    const computedStyle = getComputedStyle(document.documentElement);

    setInsets({
      top: parseInt(computedStyle.getPropertyValue('--sat') || '0'),
      right: parseInt(computedStyle.getPropertyValue('--sar') || '0'),
      bottom: parseInt(computedStyle.getPropertyValue('--sab') || '0'),
      left: parseInt(computedStyle.getPropertyValue('--sal') || '0'),
    });
  }, []);

  return insets;
}

/**
 * Responsive font size calculator
 */
export function useFluidFontSize(
  minSize: number,
  maxSize: number,
  minViewport: number = 320,
  maxViewport: number = 1200
) {
  const { width } = useWindowSize();

  const fontSize = React.useMemo(() => {
    if (width <= minViewport) return minSize;
    if (width >= maxViewport) return maxSize;

    const viewportRange = maxViewport - minViewport;
    const sizeRange = maxSize - minSize;
    const viewportProgress = (width - minViewport) / viewportRange;

    return minSize + sizeRange * viewportProgress;
  }, [width, minSize, maxSize, minViewport, maxViewport]);

  return `${fontSize}px`;
}

/**
 * Responsive spacing multiplier
 */
export function useResponsiveSpacing(baseSpacing: number = 4) {
  return useResponsiveValue({
    base: baseSpacing,
    sm: baseSpacing * 1.25,
    md: baseSpacing * 1.5,
    lg: baseSpacing * 1.75,
    xl: baseSpacing * 2,
  });
}
